import RadioGroup from '@mui/material/RadioGroup';
import type { FunctionComponent, VoidFunctionComponent } from 'react';

import LabeledRadio from '../../generic/Forms/LabeledRadio';
import Select from '../../generic/Select/Select';
import Text from '../../generic/Text';
import type { SecurityRuleCVSSFilter, SecurityRuleLevel } from '../../../../shared/model-typings';

import type { CVSSRule } from './types';

import styles from './CVSSFilterComponent.module.less';

type Props = {
  cvssRule: CVSSRule;
  onCvssRuleChange: (rule: CVSSRule) => void;
};

type SeverityCardProps = {
  cvssRule: CVSSRule;
  onChangeMinimumCVSSLevel: (e: { value: string; label: string } | null) => void;
};

type ScoreCardProps = {
  cvssRule: CVSSRule;
  onChangeMinimumCVSSValue: (e: { value: number; label: string } | null) => void;
};

const CVSS_SCORE_RANGE = Array.from({ length: 11 }, (_, i) => i);
const CVSS_SCORE_OPTIONS = CVSS_SCORE_RANGE.map((value) => ({
  value,
  label: value.toString(),
}));

const CVSS_LEVEL_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

const CVSSCard: FunctionComponent = ({ children }) => (
  <div className={styles.cvssCard}>
    {children}
  </div>
);

const SeverityCard: VoidFunctionComponent<SeverityCardProps> = ({
  cvssRule,
  onChangeMinimumCVSSLevel,
}) => (
  <CVSSCard>
    <p>Any vulnerabilities with a CVSS severity of</p>
    <div className={styles.levelSelectWrapper}>
      <Select
        id="security-policy-cvss-level"
        options={CVSS_LEVEL_OPTIONS}
        onChange={onChangeMinimumCVSSLevel}
        value={CVSS_LEVEL_OPTIONS.find((x) => x.value === cvssRule.minimumCVSSLevel) ?? CVSS_LEVEL_OPTIONS[0]}
      />
    </div>
    <p>or higher will be flagged as an issue</p>
  </CVSSCard>
);

const ScoreCard: VoidFunctionComponent<ScoreCardProps> = ({
  cvssRule,
  onChangeMinimumCVSSValue,
}) => (
  <CVSSCard>
    <p>Any vulnerabilities with a CVSS score of</p>
    <div className={styles.scoreSelectWrapper}>
      <Select
        id="security-policy-cvss-score"
        options={CVSS_SCORE_OPTIONS}
        value={CVSS_SCORE_OPTIONS.find((score) => score.value === cvssRule.minimumCVSSValue)}
        onChange={onChangeMinimumCVSSValue}
      />
    </div>
    <p>or higher will be flagged as an issue</p>
  </CVSSCard>
);

const CVSSFilterComponent: VoidFunctionComponent<Props> = ({
  cvssRule,
  onCvssRuleChange,
}) => (
  <>
    <div className={styles.filterByRow}>
      <Text.H4>Filter By</Text.H4>
      <RadioGroup
        name="cvssFilterGroup"
        className={styles.radioGroup}
        value={cvssRule.cvssFilter}
        onChange={(_event, filterVal: string) => {
          onCvssRuleChange({
            ...cvssRule,
            cvssFilter: filterVal as SecurityRuleCVSSFilter,
          });
        }}
      >
        <LabeledRadio label="CVSS Severity" value="LEVEL" />
        <LabeledRadio label="CVSS Score" value="VALUE" />
      </RadioGroup>
    </div>
    {cvssRule.cvssFilter === 'LEVEL' ? (
      <SeverityCard
        cvssRule={cvssRule}
        onChangeMinimumCVSSLevel={(e) => {
          if (e) {
            onCvssRuleChange({
              ...cvssRule,
              minimumCVSSLevel: e.value as SecurityRuleLevel,
            });
          }
        }}
      />
    ) : (
      <ScoreCard
        cvssRule={cvssRule}
        onChangeMinimumCVSSValue={(e) => {
          if (e) {
            onCvssRuleChange({
              ...cvssRule,
              minimumCVSSValue: e.value,
            });
          }
        }}
      />
    )}
  </>
);

export default CVSSFilterComponent;
